import React, { useState, useEffect } from 'react';
import './PageTransition.css';

/**
 * Page transition wrapper component
 * Adds smooth transitions when navigating between pages
 */
const PageTransition = ({ children, type = 'fade', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const getTransitionClass = () => {
    switch (type) {
      case 'fade':
        return isVisible ? 'page-fade-enter' : '';
      case 'slide-up':
        return isVisible ? 'page-slide-up-enter' : '';
      case 'slide-down':
        return isVisible ? 'page-slide-down-enter' : '';
      case 'scale':
        return isVisible ? 'page-scale-enter' : '';
      case 'rotate':
        return isVisible ? 'page-rotate-enter' : '';
      case 'blur':
        return isVisible ? 'page-blur-enter' : '';
      case 'flip':
        return isVisible ? 'page-flip-enter' : '';
      default:
        return isVisible ? 'page-fade-enter' : '';
    }
  };

  return (
    <div className={`page-transition-container ${getTransitionClass()}`}>
      {children}
    </div>
  );
};

/**
 * Content reveal wrapper
 * Adds staggered reveal animations to child elements
 */
export const ContentReveal = ({ children, stagger = false, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className={stagger ? 'content-reveal-stagger' : 'content-reveal'}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            className: `${child.props.className || ''} ${isVisible ? 'visible' : ''}`,
          });
        }
        return child;
      })}
    </div>
  );
};

/**
 * Loading overlay component
 * Shows a loading animation during page transitions
 */
export const LoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="page-loading-overlay">
      <div className="spinner" />
    </div>
  );
};

export default PageTransition;
